import dayjs from "dayjs";
import {
  ActionIcon,
  Box,
  Button,
  Grid,
  Menu,
  Modal,
  Select,
  Table,
  Textarea,
  TextInput,
  Tooltip
} from "@mantine/core";
import { IconBan, IconDotsVertical, IconEye } from "@tabler/icons-react";
import AppPagination from "@app/common/components/AppPagination";
import { warningNotification } from "@app/common/components/notifications";
import { FilterSearchButton } from "@app/common/components/SearchButtonFilters";
import { Template } from "@app/common/components/Template";
import { TextField } from "@app/common/components/TextField";
import { formattedDataSelect, formatUpdatedInfo } from "@app/common/utils/utils";
import { GCidAccount } from "@app/config/globalsConst";
import routes from "@app/config/routes";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetSiteListToStart, useGetCancellationReasons } from "@app/services/common/hooks";
import { useAuthentication } from "@app/store/useAuthtentication";
import { useDeliveryData } from "@app/store/useDeliverySearchData";
import {
  useDownloadPharmacyControlExcel,
  useGetPharmacyControlList,
  useRequestAnnulDelivery
} from "@app/services/pharmacyControl/hooks";
import {
  PharmacyControlFilter,
  PharmacyControlResponse
} from "@app/services/pharmacyControl/types";
import { downloadExcel } from "@app/utils/downloadExcel";
import { usePermissions } from "@app/common/hooks/usePermissions";
import {
  RBAC_ACTIONS,
  RBAC_LABELS,
  MODULE_PERMISSION_PREFIX
} from "@app/common/utils/permissionsConstants";

const TODAY = dayjs().format("YYYY-MM-DD");

const PharmacyControl = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canDelete = can(RBAC_ACTIONS.DELETE, MODULE_PERMISSION_PREFIX.PHARMACY_CONTROL);

  const userId = useAuthentication((state) => state.auth.dataBiowel.idUser);

  const { dataDispense, setDataDispense } = useDeliveryData((store) => store);

  const [filters, setFilters] = useState<PharmacyControlFilter>({
    search: "",
    site: "",
    startDate: TODAY,
    endDate: TODAY
  });
  const [dateFilters, setDateFilters] = useState<PharmacyControlFilter>({
    startDate: TODAY,
    endDate: TODAY
  });
  const [page, setPage] = useState<number>(1);
  const [annulModal, setAnnulModal] = useState<{
    isOpen: boolean;
    disId: number | null;
    attributableTo: string | null;
    radReasonId: string | null;
    radReasonText: string;
  }>({
    isOpen: false,
    disId: null,
    attributableTo: null,
    radReasonId: null,
    radReasonText: ""
  });

  const { data: supportList, rowTotal } = useGetPharmacyControlList({
    ...filters,
    page,
    perpage: 10
  });

  const { data: sitesList } = useGetSiteListToStart({
    eaccount: GCidAccount(),
    userId,
    status: 1
  });

  const { refetch: getPharmacyControlExcel } = useDownloadPharmacyControlExcel({
    ...filters,
    page: undefined,
    perpage: undefined
  });

  const { mutateAsync: requestAnnulDelivery, isPending: isAnnulPending } = useRequestAnnulDelivery(
    async () => {
      setAnnulModal({
        isOpen: false,
        disId: null,
        attributableTo: null,
        radReasonId: null,
        radReasonText: ""
      });
    }
  );

  const { data: cancellationReasons } = useGetCancellationReasons(
    annulModal.attributableTo ?? "",
    !!annulModal.attributableTo
  );

  const handleSubmitFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    const site = formData.get("site")?.toString() || undefined;
    const search = formData.get("search")?.toString() || undefined;
    const startDate = formData.get("date-start")?.toString() || undefined;
    const endDate = formData.get("date-end")?.toString() || undefined;

    setFilters({
      site,
      search,
      startDate,
      endDate
    });
    setPage(1);
  };

  const handleDownloadExcel = async () => {
    const response = await getPharmacyControlExcel();

    const { success, results } = response.data || {};

    if (success && results?.base64) {
      downloadExcel(results.base64, "control_farmacia.xlsx");
    } else {
      warningNotification("No se pudo descargar el archivo");
    }
  };

  const handleOpenAnnulModal = (disId: number) => {
    setAnnulModal({
      isOpen: true,
      disId,
      attributableTo: null,
      radReasonId: null,
      radReasonText: ""
    });
  };

  const handleAnnulDelivery = async () => {
    if (!canDelete) {
      return;
    }
    if (!annulModal.disId || !annulModal.radReasonId || !annulModal.radReasonText.trim()) {
      return;
    }
    await requestAnnulDelivery({
      disId: annulModal.disId,
      radReasonId: Number(annulModal.radReasonId),
      radReasonText: annulModal.radReasonText
    });
  };

  const renderFiltersHeader = () => {
    return (
      <Grid align="flex-end" mb="lg" w="100%" columns={12} data-testid="pharmacycontrol-filters">
        <Grid.Col span={2}>
          <Select
            data-testid="pharmacycontrol-site-select"
            label="Sede"
            data={formattedDataSelect(sitesList?.results.sites ?? [], "siteName", "siteId")}
            searchable
            placeholder="Seleccionar..."
            name="site"
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput
            data-testid="pharmacycontrol-start-date-input"
            label="Fecha desde"
            type="date"
            max={TODAY}
            placeholder="Escribir..."
            name="date-start"
            value={dateFilters.startDate}
            onChange={({ target }) => {
              setDateFilters((state) => ({
                ...state,
                startDate: target.value
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput
            data-testid="pharmacycontrol-end-date-input"
            label="Fecha hasta"
            type="date"
            min={dateFilters.startDate}
            max={TODAY}
            placeholder="Escribir..."
            name="date-end"
            value={dateFilters.endDate}
            onChange={({ target }) => {
              setDateFilters((state) => ({
                ...state,
                endDate: target.value
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <TextField
            data-testid="pharmacycontrol-search-input"
            placeholder="Escribe aquí para buscar..."
            variant="filled"
            className="flex-grow-1 me-4"
            name="search"
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <FilterSearchButton data-testid="pharmacycontrol-search-button" />
        </Grid.Col>
        <Grid.Col span="auto" className="flex justify-end">
          <img
            src="/excel.svg"
            alt="download excel"
            className="pointer"
            data-testid="pharmacycontrol-download-excel"
            onClick={handleDownloadExcel}
          />
        </Grid.Col>
      </Grid>
    );
  };

  const renderRow = (option: PharmacyControlResponse, index: number) => {
    const date = (option.deliveryDate ?? "").split("T")[0].replaceAll("/", "-");
    const updatedInfo = formatUpdatedInfo(option.lastLotUpdateBy?.name, option.lastLotUpdateAt);
    const isToday = date === TODAY;

    return (
      <Table.Tr key={index} data-testid={`pharmacycontrol-row-${index}`}>
        <Table.Td>{option.patient.document}</Table.Td>
        <Table.Td>{option.patient.name}</Table.Td>
        <Table.Td ta={"start"}>{option.orderedBy?.name}</Table.Td>
        <Table.Td ta={"start"}>{option.site}</Table.Td>
        <Table.Td ta={"center"}>{option.diagnostic}</Table.Td>
        <Table.Td ta={"start"}>{option.medicine.name}</Table.Td>
        <Table.Td ta={"center"}>{option.medicine.qty_delivered}</Table.Td>
        <Table.Td ta={"center"}>{date}</Table.Td>
        <Table.Td ta={"start"}>{updatedInfo}</Table.Td>
        <Table.Td ta={"center"}>{option.deliveryBy.name}</Table.Td>
        <Table.Td ta={"center"}>{option.receivedBy}</Table.Td>
        <Table.Td ta="center">
          <Menu position="bottom-end" withArrow data-testid={`pharmacycontrol-menu-${index}`}>
            <Menu.Target>
              <ActionIcon variant="subtle" data-testid={`pharmacycontrol-menu-button-${index}`}>
                <IconDotsVertical />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconEye />}
                data-testid={`pharmacycontrol-detail-item-${index}`}
                onClick={() => {
                  setDataDispense({
                    ...dataDispense,
                    deliveryBy: "insurance",
                    disId: option.disId,
                    orderId: option.morId,
                    withoutOrder: !option.morId,
                    action: "detail",
                    medicalOrderType: option.medicalOrderType
                  });
                  navigate(routes.getPharmacyControlDetail);
                }}
              >
                Ver detalle
              </Menu.Item>
              {isToday && (
                <Tooltip
                  label={canDelete ? "Eliminar entrega" : RBAC_LABELS.NO_PERMISSION}
                  withArrow
                  position="left"
                >
                  <Menu.Item
                    leftSection={<IconBan />}
                    color={canDelete ? "red" : "gray"}
                    disabled={!canDelete}
                    className={!canDelete ? "noPermissionDisabled" : ""}
                    data-testid={`pharmacycontrol-delete-item-${index}`}
                    onClick={() => {
                      if (!canDelete) {
                        return;
                      }
                      handleOpenAnnulModal(option.disId);
                    }}
                  >
                    Eliminar entrega
                  </Menu.Item>
                </Tooltip>
              )}
            </Menu.Dropdown>
          </Menu>
        </Table.Td>
      </Table.Tr>
    );
  };

  const renderTable = () => {
    return (
      <Table className="biowel-table" data-testid="pharmacycontrol-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th ta={"start"}>Identificación</Table.Th>
            <Table.Th ta={"start"}>Paciente</Table.Th>
            <Table.Th ta={"start"}>Médico</Table.Th>
            <Table.Th ta={"start"}>Sede</Table.Th>
            <Table.Th ta={"start"}>Diagnóstico</Table.Th>
            <Table.Th ta={"start"}>Medicamento</Table.Th>
            <Table.Th ta={"center"}>Cantidad</Table.Th>
            <Table.Th ta={"center"}>F. entrega</Table.Th>
            <Table.Th ta={"start"}>Última actualización</Table.Th>
            <Table.Th ta={"center"}>Entregado por</Table.Th>
            <Table.Th ta={"center"}>Recibido por</Table.Th>
            <Table.Th> </Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {supportList.length ? (
            supportList.map(renderRow)
          ) : (
            <Table.Tr>
              <Table.Td ta="center" colSpan={12} data-testid="pharmacycontrol-no-data">
                No hay información para mostrar.
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    );
  };

  const renderAnnulModal = () => (
    <Modal
      opened={annulModal.isOpen}
      onClose={() =>
        setAnnulModal({
          isOpen: false,
          disId: null,
          attributableTo: null,
          radReasonId: null,
          radReasonText: ""
        })
      }
      title="Solicitar anulación de entrega"
      size="lg"
      data-testid="pharmacycontrol-annul-modal"
    >
      <Box className="flex flex-column gap-3" data-testid="pharmacycontrol-annul-modal-content">
        <Select
          label="Atribuible a"
          placeholder="Seleccionar..."
          withAsterisk
          data={[
            { value: "institution", label: "Institución" },
            { value: "patient", label: "Paciente" }
          ]}
          value={annulModal.attributableTo}
          data-testid="pharmacycontrol-annul-attributable-to"
          onChange={(val) =>
            setAnnulModal((s) => ({ ...s, attributableTo: val, radReasonId: null }))
          }
        />
        <Select
          label="Motivo de anulación"
          placeholder="Seleccionar..."
          searchable
          withAsterisk
          disabled={!annulModal.attributableTo}
          data={formattedDataSelect(cancellationReasons, "canDescription", "canId")}
          value={annulModal.radReasonId}
          data-testid="pharmacycontrol-annul-reason-id"
          onChange={(val) => setAnnulModal((s) => ({ ...s, radReasonId: val }))}
        />
        <Textarea
          label="Descripción de la razón"
          placeholder="Escribir..."
          withAsterisk
          rows={4}
          value={annulModal.radReasonText}
          data-testid="pharmacycontrol-annul-reason-text"
          onChange={({ target }) => setAnnulModal((s) => ({ ...s, radReasonText: target.value }))}
        />
        <Box className="flex justify-end gap-3" pt="sm">
          <Button
            variant="outline"
            data-testid="pharmacycontrol-annul-cancel-button"
            onClick={() =>
              setAnnulModal({
                isOpen: false,
                disId: null,
                attributableTo: null,
                radReasonId: null,
                radReasonText: ""
              })
            }
          >
            Cancelar
          </Button>
          <Button
            variant="filled"
            color="red"
            loading={isAnnulPending}
            disabled={
              !annulModal.attributableTo ||
              !annulModal.radReasonId ||
              !annulModal.radReasonText.trim()
            }
            data-testid="pharmacycontrol-annul-confirm-button"
            onClick={handleAnnulDelivery}
          >
            Confirmar anulación
          </Button>
        </Box>
      </Box>
    </Modal>
  );

  return (
    <Template title="Control de farmacia" data-testid="pharmacycontrol-template">
      {renderAnnulModal()}
      <form onSubmit={handleSubmitFilters} data-testid="pharmacycontrol-filters-form">
        {renderFiltersHeader()}
      </form>

      {renderTable()}

      <AppPagination
        data-testid="pharmacycontrol-pagination"
        total={rowTotal}
        value={page}
        withDetails
        onChange={(value) => setPage(value)}
      />
    </Template>
  );
};

export default PharmacyControl;